import { useEffect, useRef } from 'react';
import type { PatientPhoneStatus, SipCallState } from '@doxyme/types';
import { CallState, CallTransition } from '../../../features/call/call.types';
import type { TransitionFunc } from '../useCall.types';
import { useCallParticipants } from '../../useCallParticipants';
import { getAllSessionParticipantsMap } from '../../../features/session/session.selectors';
import { useAppSelector } from '../../useReduxToolkit';
import { getLogger } from '../../../libs/logger';

const logger = getLogger('useCallStatePhoneCallParticipants');

/** Vonage Voice API terminal statuses that mean the callee line was busy. */
const PATIENT_PHONE_BUSY_STATUSES: PatientPhoneStatus[] = [
  'busy',
  'rejected',
  'failed',
  'timeout',
];

type SipParticipantSnapshot = {
  id: string;
  sipCallState?: SipCallState | null;
  patientPhoneStatus?: PatientPhoneStatus;
};

/**
 * Watches outbound SIP phone participants while the call is ringing or in progress,
 * and drives the phone-call slice of the call state machine from their Vonage leg status.
 */
export const useCallStatePhoneCallParticipants = (
  transition: TransitionFunc,
  callState: CallState,
): void => {
  const { sipCallParticipants } = useCallParticipants();
  const allParticipantsMap = useAppSelector(getAllSessionParticipantsMap);

  const allSipCallParticipants = Object.values(allParticipantsMap).filter(
    (participant) => participant.isSipParticipant,
  ) as SipParticipantSnapshot[];

  const isRinging = callState === CallState.PhoneCallRinging;
  const isPhoneCallInProgress = callState === CallState.PhoneCallInProgress;
  const isPhoneCallMonitored = isRinging || isPhoneCallInProgress;

  // Each phase fires at most one transition per ringing / in-progress cycle.
  const hasEmittedRingingTransition = useRef(false);
  const hasEmittedInProgressTransition = useRef(false);

  // While ringing, only inspect SIP participants that were present when ringing started.
  const ringingSipParticipantIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isPhoneCallMonitored) {
      hasEmittedRingingTransition.current = false;
      hasEmittedInProgressTransition.current = false;
      ringingSipParticipantIds.current.clear();
      return;
    }

    if (isRinging) {
      for (const participant of sipCallParticipants) {
        ringingSipParticipantIds.current.add(participant.id);
      }
    }
  }, [isPhoneCallMonitored, isRinging, sipCallParticipants]);

  useEffect(() => {
    if (!isPhoneCallMonitored) {
      return;
    }

    // Once a phase has emitted its transition, ignore later status updates
    // (e.g. HANGUP + busy latched, then status changes to complete).
    if (isRinging && hasEmittedRingingTransition.current) {
      return;
    }
    if (isPhoneCallInProgress && hasEmittedInProgressTransition.current) {
      return;
    }

    const ringingIds = ringingSipParticipantIds.current;
    const participantsToInspect = isRinging
      ? allSipCallParticipants.filter((participant) =>
          ringingIds.has(participant.id),
        )
      : allSipCallParticipants;

    for (const participant of participantsToInspect) {
      const { sipCallState, patientPhoneStatus } = participant;

      let nextTransition: CallTransition | undefined;
      let logMessage = '';

      if (isRinging) {
        // Conditions are evaluated in priority order; the first match wins.
        if (sipCallState === 'ACTIVE' && patientPhoneStatus === 'answered') {
          nextTransition = CallTransition.PHONE_PARTICIPANT_ANSWERED;
          logMessage =
            'Patient answered outbound phone call; emitting PHONE_PARTICIPANT_ANSWERED';
        } else if (
          sipCallState === 'HANGUP' &&
          (patientPhoneStatus === 'unanswered' ||
            patientPhoneStatus === 'cancelled')
        ) {
          nextTransition = CallTransition.PHONE_CALL_PATIENT_UNANSWERED;
          logMessage =
            'Patient did not answer outbound phone call; emitting PHONE_CALL_PATIENT_UNANSWERED';
        } else if (
          sipCallState === 'HANGUP' &&
          patientPhoneStatus !== undefined &&
          (PATIENT_PHONE_BUSY_STATUSES.includes(patientPhoneStatus) ||
            patientPhoneStatus === 'cancelled')
        ) {
          nextTransition = CallTransition.PHONE_CALL_PATIENT_BUSY;
          logMessage =
            'Patient phone line busy; emitting PHONE_CALL_PATIENT_BUSY';
        }
      } else if (
        sipCallState === 'HANGUP' &&
        (patientPhoneStatus === 'complete' ||
          (patientPhoneStatus as string | undefined) === 'completed')
      ) {
        nextTransition = CallTransition.PHONE_CALL_PATIENT_LEFT;
        logMessage =
          'Patient left connected phone call; emitting PHONE_CALL_PATIENT_LEFT';
      }

      if (!nextTransition) {
        continue;
      }

      if (isRinging) {
        hasEmittedRingingTransition.current = true;
      } else {
        hasEmittedInProgressTransition.current = true;
      }

      logger.info(logMessage, {
        participantId: participant.id,
        sipCallState,
        patientPhoneStatus,
        callState,
      });
      void transition(nextTransition);
      return;
    }
  }, [
    allSipCallParticipants,
    callState,
    isPhoneCallInProgress,
    isPhoneCallMonitored,
    isRinging,
    transition,
  ]);
};
